import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { createPath, ROUTE } from "app-constants/routing"
import { ActivityIndicator } from "components/activity-indicator"
import { NoResultsFoundFallback } from "components/common/NoResultsFoundFallback"
import SmallVideoCard from "components/videos/SmallVideoCard"
import { UseMoreParams } from "models/convention-api/types"
import ConventionVideo from "models/convention-api/v1/ConventionVideo"
import VideoCount from "models/convention-api/v1/VideoCount"
import pluralize from "pluralize"
import React, { useCallback, useRef } from "react"

const useStyles = makeStyles()(
  (theme) => ({
    container: {
      display: "flex",
      alignItems: "center",
      flexDirection: "column",

      width: "100%",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    resultsContainer: {
      width: "100%",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    resultsTypography: {
      ...theme.fonts.lato400,
      fontSize: 16,
      lineHeight: 1.375,
    },
    videosContainer: {
      display: "grid",
      justifyContent: "center",
      gridGap: theme.spacing(1.8),
      gridTemplateColumns: "1fr",

      width: "100%",

      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
      },
    },
    loadingIndicatorContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: theme.spacing(2),
    },
  }),
)

interface VideosSearchResultsProps {
  filter: UseMoreParams["filter"]
}

export const VideosSearchResults = (props: VideosSearchResultsProps) => {
  const { filter } = props
  const { classes } = useStyles()

  const videoResultCounts = VideoCount.useOne({ filter })

  const videos = ConventionVideo.useMore({
    limit: 20,
    filter: filter,
    includes: ["institution", "video_count"],
  })

  const observer = useRef<IntersectionObserver>(null)
  const loadingIndicatorContainerRef = useCallback(
    (node: HTMLElement | null) => {
      if (videos.isLoading) return
      if (observer.current) observer.current.disconnect()
      // @ts-ignore
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && videos.hasMore) videos.loadMore()
      })
      if (node) observer.current.observe(node)
    },
    [videos.isLoading, videos.hasMore],
  )

  if (!videos.data || !videoResultCounts.data) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loadingIndicatorContainer}>
          <ActivityIndicator />
        </Box>
      </Box>
    )
  }

  if (videos.data.length === 0 && !videos.isLoading) {
    return (
      <Box className={classes.container}>
        <NoResultsFoundFallback />
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.resultsContainer}>
        <Typography className={classes.resultsTypography}>
          {pluralize(`${videoResultCounts.data.count} results`, videoResultCounts.data.count)}
        </Typography>
      </Box>
      <Box className={classes.videosContainer}>
        {videos.data?.map((video) => (
          <SmallVideoCard
            key={video.id}
            href={createPath({ path: ROUTE.VIDEO, params: { videoId: video.id } })}
            imageSrc={video.thumbnail_url ?? ""}
            imageAlt=""
            curatorName={video.institution?.name ?? ""}
            title={video.name}
            numberOfViews={video.video_count?.video_count ?? 0}
          />
        ))}
      </Box>
      {(videos.hasMore || videos.isLoading) && (
        <Box
          className={classes.loadingIndicatorContainer}
          {...{ ref: loadingIndicatorContainerRef }}>
          <ActivityIndicator />
        </Box>
      )}
    </Box>
  )
}
